<script setup>
import { computed } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useBarcodeGenerator } from '@/composables/useBarcodeGenerator';

const authStore = useAuthStore();
const {
  skusText, rows,
  isGenerating, error,
  generate, exportToExcel, reset,
} = useBarcodeGenerator();

const hasResults = computed(() => rows.value.some(r => r.barcode !== null));

function closePopup() {
  authStore.toggleBarcodeGenerator();
  reset();
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div
        v-show="authStore.barcodeGeneratorVisible"
        class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center items-start px-8 z-[100]"
        @click.self="closePopup"
      >
        <Transition name="modal-inner">
          <div
            v-if="authStore.barcodeGeneratorVisible"
            class="fixed top-16 z-[101] w-full max-w-2xl"
          >
            <div class="bg-white rounded-2xl shadow-lg p-6 w-full max-h-[85vh] overflow-y-auto">

              <!-- Header -->
              <div class="flex justify-between items-center mb-6">
                <div class="flex items-center gap-3">
                  <h2 class="font-semibold text-[24px]">Barcode Generator</h2>
                  <span class="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md">UPC-A</span>
                </div>
                <button type="button" @click="closePopup" class="text-gray-500 hover:text-gray-700">
                  <i class="pi pi-times text-xl"></i>
                </button>
              </div>

              <!-- SKU input -->
              <div class="flex flex-col gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    SKUs <span class="font-normal text-gray-400">— paste one per line</span>
                  </label>
                  <textarea
                    v-model="skusText"
                    rows="5"
                    placeholder="SKU-001&#10;SKU-002&#10;SKU-003"
                    class="w-full px-4 py-3 bg-gray-100 border-none rounded-lg outline-none text-sm font-mono resize-y"
                  />
                  <p v-if="rows.length > 0" class="mt-1 text-xs text-gray-400">
                    {{ rows.length }} SKU{{ rows.length !== 1 ? 's' : '' }} detected
                  </p>
                </div>

                <!-- Error -->
                <div
                  v-if="error"
                  class="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600"
                >
                  <i class="pi pi-exclamation-triangle shrink-0"></i>
                  {{ error }}
                </div>

                <!-- Generate button -->
                <div class="flex justify-end">
                  <button
                    type="button"
                    :disabled="isGenerating || rows.length === 0"
                    class="flex items-center gap-2 px-6 py-3 bg-[#7C3AED] text-white text-sm font-medium rounded-lg
                           hover:bg-[#6D28D9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="generate"
                  >
                    <i v-if="isGenerating" class="pi pi-spin pi-spinner"></i>
                    <i v-else class="pi pi-barcode"></i>
                    {{ isGenerating ? 'Generating…' : 'Generate' }}
                  </button>
                </div>
              </div>

              <!-- Results grid -->
              <div v-if="rows.length > 0" class="mt-6 border-t border-gray-100 pt-5">

                <!-- Table -->
                <div class="overflow-auto rounded-lg border border-gray-200 max-h-72">
                  <table class="w-full text-sm">
                    <thead>
                      <tr class="bg-gray-50 border-b border-gray-200">
                        <th class="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                        <th class="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">SKU</th>
                        <th class="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Barcode</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, index) in rows"
                        :key="row.sku"
                        class="border-b border-gray-100 last:border-0 bg-white"
                      >
                        <td class="px-3 py-2 text-gray-400">{{ row.seq }}</td>
                        <td class="px-3 py-2 text-gray-700">{{ row.sku }}</td>
                        <td class="px-3 py-2 font-mono text-gray-800">
                          <span v-if="row.barcode">{{ row.barcode }}</span>
                          <span v-else class="text-gray-300">—</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Footer -->
                <div class="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <p class="text-sm text-gray-500">
                    {{ rows.length }} SKU{{ rows.length !== 1 ? 's' : '' }}
                    <template v-if="hasResults">
                      · <span class="text-green-600 font-medium">barcodes assigned</span>
                    </template>
                  </p>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      @click="reset"
                    >
                      <i class="pi pi-refresh text-xs"></i>
                      Reset
                    </button>
                    <button
                      type="button"
                      :disabled="!hasResults"
                      class="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      @click="exportToExcel"
                    >
                      <i class="pi pi-download text-xs"></i>
                      Export Excel
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  transform: scale(0.95);
  opacity: 0;
}

.modal-inner-leave-to {
  transform: scale(0.95);
  opacity: 0;
}

/* Remove number input spin arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
